import React from 'react'
import TicketSelector from './TicketSelector'
import Image from './Image'
import Button from './Button'
import Title from './Title'
import Video from './Video'
import Text from './Text'
import PurchaseForm from './PurchaseForm'
import VisibleContent, {VisibleContentProps} from './VisibleContent'
import Separator from './Separator'
import CountdownTimer from './CountdownTimer'
import BulletedList from './BulletedList'
import NumberedList from './NumberedList'
import Icon from './Icon'
import FaqList from './FaqList'
import Spacer from './Spacer'
import UpsellPurchaseButton from './UpsellPurchaseButton'
import UpsellDeclineButton from './UpsellDeclineButton'
import {PageTemplate} from '../page-template'
import UpsellDeclineText from './UpsellDeclineText'
import {NLPurchasePageBlock} from '../nl-purchase-page'
import NLSection from './NLSectionBlock'

export interface NLBlockProps extends BlockComponentProps {
  block: NLPurchasePageBlock
  children?: React.ReactNode
  isMobileMode?: boolean
  CustomComponents?: CustomComponents
}

export interface BlockComponentProps {
  id: string
  template: PageTemplate
  CustomComponents?: CustomComponents
  width?: number
  height?: number
  isEditMode?: boolean
}

interface CustomComponents {
  Image?: any
}

const NLBlock = React.forwardRef<HTMLDivElement, NLBlockProps>((props, ref) => {
  const {
    block,
    id,
    isMobileMode,
    template,
    CustomComponents,
    children,
    width,
    height,
  } = props

  const Component = Components[block.type] ?? null

  if (!Component) {
    return null
  }

  return (
    <Box
      className="block"
      ref={ref}
      visibility={block.visibility}
      isMobileMode={isMobileMode}
      style={{
        transitionProperty: 'none', // disable drag animations
        width,
        height,
      }}
    >
      {/*
       * Setting 'any' type for props is ok here, because we're
       * already verifying the correct component receives the
       * correct type in ComponentDefinitions.
       */}
      <Component
        {...(block as any)}
        id={id}
        template={template}
        CustomComponents={CustomComponents}
      />
      {children}
    </Box>
  )
})

export default NLBlock

export type ComponentDefinitions = {
  [T in NLPurchasePageBlock as T['type']]: React.FC<
    T & {id: string; template: PageTemplate}
  >
}

export const Components: ComponentDefinitions = {
  Title,
  Text,
  Image,
  TicketSelector,
  Button,
  Video,
  PurchaseForm,
  Separator,
  BulletedList,
  CountdownTimer,
  NumberedList,
  Icon,
  FaqList,
  Spacer,
  UpsellPurchaseButton,
  UpsellDeclineButton,
  UpsellDeclineText,
  Section: NLSection,
}

export const Box = React.forwardRef<HTMLDivElement, VisibleContentProps>(
  (props, ref) => {
    return (
      <VisibleContent
        {...props}
        ref={ref}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          ...props.style,
        }}
      />
    )
  },
)
